import { REGISTRATION_PROFILE_JSON_KEY } from '@buxus/shared'

export type LoggedInPerson = {
  name: string
  email: string
  /** ABP Identity user id when bootstrap has it. */
  userId?: string
  /** From registration profile when present. */
  phone?: string
  /** jobRoleTitle / title from registration profile. */
  jobTitle?: string
  /** Registration profile last name, when that field is populated. */
  lastName?: string
  agencyCity?: string
  agencyState?: string
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
}

function str(rec: Record<string, unknown> | null, key: string): string {
  if (!rec) return ''
  const v = rec[key]
  return typeof v === 'string' ? v.trim() : ''
}

/** Cached `get-registration-profile` blob, or null if missing/invalid. */
export function readRegistrationProfileJson(): unknown | null {
  try {
    const raw = localStorage.getItem(REGISTRATION_PROFILE_JSON_KEY)
    if (!raw?.trim()) return null
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}

export function profileFieldsFromBlob(data: unknown): {
  first: string
  last: string
  phone: string
  jobTitle: string
  agencyCity: string
  agencyState: string
  email: string
} {
  const root = asRecord(data)
  if (!root) {
    return { first: '', last: '', phone: '', jobTitle: '', agencyCity: '', agencyState: '', email: '' }
  }
  const account = asRecord(root.account) ?? asRecord(root.Account)
  const nested =
    asRecord(root.data) ?? asRecord(root.registration) ?? asRecord(root.Registration)
  const layers = [account, nested, root].filter((layer): layer is Record<string, unknown> => !!layer)
  const pick = (keys: readonly string[]): string => {
    for (const layer of layers) {
      for (const key of keys) {
        const value = str(layer, key)
        if (value && !value.includes('@')) return value
      }
    }
    return ''
  }
  let first = pick(['firstName', 'first_Name', 'FirstName', 'givenName', 'GivenName'])
  let last = pick([
    'lastName',
    'last_Name',
    'LastName',
    'surname',
    'Surname',
    'surName',
    'familyName',
    'FamilyName',
  ])
  if (!first || !last) {
    const full = pick(['fullName', 'FullName'])
    const parts = full.split(/\s+/).filter(Boolean)
    if (!first && parts[0]) first = parts[0]
    if (!last && parts.length > 1) last = parts.slice(1).join(' ')
  }
  const rec = account ?? nested ?? root
  const phone =
    str(rec, 'phone') ||
    str(rec, 'Phone') ||
    str(rec, 'mobile') ||
    str(rec, 'Mobile') ||
    str(rec, 'cellPhone') ||
    str(rec, 'CellPhone') ||
    str(root, 'phone') ||
    str(root, 'Phone')
  const jobTitle =
    str(rec, 'jobRoleTitle') ||
    str(rec, 'jobTitle') ||
    str(rec, 'title') ||
    str(root, 'jobRoleTitle') ||
    str(root, 'title')
  const agencyCity = str(rec, 'agencyCity') || str(root, 'agencyCity')
  const agencyState = str(rec, 'state') || str(root, 'state') || str(rec, 'agencyState')
  const email = str(rec, 'email') || str(root, 'email')
  return { first, last, phone, jobTitle, agencyCity, agencyState, email }
}

/**
 * Debug / merge helper: platform profile for an email when it matches the
 * cached registration blob (POC only has a profile for the signed-in user).
 */
export function platformProfileDebugForEmail(email: string): {
  matched: boolean
  loggedInSlice: LoggedInPerson | null
  registrationProfile: unknown | null
} {
  const key = email.trim().toLowerCase()
  const blob = readRegistrationProfileJson()
  const fields = profileFieldsFromBlob(blob)
  const blobEmail = fields.email.trim().toLowerCase()
  const matched = Boolean(key && blobEmail && key === blobEmail)
  if (!matched) {
    return { matched: false, loggedInSlice: null, registrationProfile: null }
  }
  const full = `${fields.first} ${fields.last}`.trim()
  return {
    matched: true,
    loggedInSlice: {
      name: full || fields.email,
      email: fields.email,
      ...(fields.phone ? { phone: fields.phone } : {}),
      ...(fields.jobTitle ? { jobTitle: fields.jobTitle } : {}),
      ...(fields.last ? { lastName: fields.last } : {}),
      ...(fields.agencyCity ? { agencyCity: fields.agencyCity } : {}),
      ...(fields.agencyState ? { agencyState: fields.agencyState } : {}),
    },
    registrationProfile: blob,
  }
}

/** Account email. A different address, including one that only adds digits, is a different user. */
export function preferredLoginAddress(
  email: string | null | undefined,
  userName: string | null | undefined,
): string {
  const mail = email?.trim() || ''
  const name = userName?.trim() || ''
  if (mail.includes('@')) return mail
  if (name.includes('@')) return name
  return mail || name
}

/** Exact address match. Case does not matter. Extra digits are a different user. */
export function sameLoginEmail(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = a?.trim().toLowerCase() ?? ''
  const right = b?.trim().toLowerCase() ?? ''
  return left.length > 0 && left === right
}

export function loggedInPerson(
  bootstrap: {
    userId?: string | null
    userName?: string | null
    email?: string | null
    givenName?: string | null
    surname?: string | null
  } | null,
): LoggedInPerson {
  const userName = bootstrap?.userName?.trim() || ''
  const fields = profileFieldsFromBlob(readRegistrationProfileJson())
  const email = preferredLoginAddress(bootstrap?.email, userName)
  const accountGiven = bootstrap?.givenName?.trim() || ''
  const accountFamily = bootstrap?.surname?.trim() || ''
  const accountName = [accountGiven, accountFamily].filter((part) => part && !part.includes('@')).join(' ')
  const profileName = [fields.first, fields.last].filter(Boolean).join(' ')
  const full =
    (accountName.split(/\s+/).filter(Boolean).length > 1 ? accountName : '') ||
    profileName ||
    accountName
  const emailOut = email || fields.email || (userName.includes('@') ? userName : '')
  const name =
    full || (userName && !userName.includes('@') ? userName : '') || emailOut || 'Trainer'
  const lastName = fields.last || accountFamily || undefined
  const userId = bootstrap?.userId?.trim() || undefined
  return {
    name,
    email: emailOut || userName,
    ...(userId ? { userId } : {}),
    ...(fields.phone ? { phone: fields.phone } : {}),
    ...(fields.jobTitle ? { jobTitle: fields.jobTitle } : {}),
    ...(lastName ? { lastName } : {}),
    ...(fields.agencyCity ? { agencyCity: fields.agencyCity } : {}),
    ...(fields.agencyState ? { agencyState: fields.agencyState } : {}),
  }
}
